class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (event) => {
      if (event.target.id === 'CartDrawer-Overlay') this.close();
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || cartLink === this.cartLink) return;
    if (this.cartLink) {
      this.cartLink.removeEventListener('click', this.cartLinkClick);
      this.cartLink.removeEventListener('keydown', this.cartLinkKeydown);
    }
    this.cartLink = cartLink;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    this.cartLinkClick = (event) => {
      event.preventDefault();
      this.open(cartLink);
    };
    this.cartLinkKeydown = (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    };
    cartLink.addEventListener('click', this.cartLinkClick);
    cartLink.addEventListener('keydown', this.cartLinkKeydown);
  }

  connectedCallback() {
    this.setHeaderCartIconAccessibility();
    this.editorSelect = (event) => {
      if (event.target.contains(this)) this.open();
    };
    this.editorDeselect = (event) => {
      if (event.target.contains(this)) this.close();
    };
    document.addEventListener('shopify:section:select', this.editorSelect);
    document.addEventListener('shopify:section:deselect', this.editorDeselect);
  }

  disconnectedCallback() {
    clearTimeout(this.openTimeout);
    this.cartLink?.removeEventListener('click', this.cartLinkClick);
    this.cartLink?.removeEventListener('keydown', this.cartLinkKeydown);
    this.cartLink = null;
    document.removeEventListener('shopify:section:select', this.editorSelect);
    document.removeEventListener('shopify:section:deselect', this.editorDeselect);
    if (this.classList.contains('active')) this.close();
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.inert = false;
    this.setAttribute('aria-hidden', 'false');
    if (this.classList.contains('active')) {
      this.focusContents();
      return;
    }
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.openTimeout = setTimeout(() => {
      this.classList.add('animate', 'active');
      this.focusContents();
    });

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    clearTimeout(this.openTimeout);
    this.classList.remove('active');
    removeTrapFocus(this.activeElement?.isConnected ? this.activeElement : document.querySelector('#cart-icon-bubble'));
    this.inert = true;
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
  }

  focusContents(element) {
    const container = this.querySelector('.drawer__inner');
    if (container) trapFocus(container, element || container);
  }

  setBusy(busy) {
    this.setAttribute('aria-busy', String(busy));
    this.querySelector('#CartDrawer-CartItems')?.classList.toggle('cart__items--disabled', busy);
    const checkout = this.querySelector('#CartDrawer-Checkout');
    if (checkout) checkout.disabled = busy || this.classList.contains('is-empty');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async resolveSections(sections = {}, expectItems = false) {
    sections ||= {};
    const valid = (html) => {
      const source = this.getSectionDOM(html || '', 'cart-drawer');
      return source?.querySelector('#CartDrawer .drawer__inner') && !(expectItems && source.classList.contains('is-empty'));
    };
    if (valid(sections['cart-drawer'])) return sections;
    const response = await fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) throw new Error(window.cartStrings.error);
    const recovered = await response.json();
    if (!valid(recovered['cart-drawer'])) {
      throw new Error(window.cartStrings.error);
    }
    return { ...sections, ...recovered };
  }

  async renderContents(parsedState) {
    try {
      parsedState.sections = await this.resolveSections(parsedState.sections, !!(parsedState.id || parsedState.items?.length));
    } catch {
      window.location.assign(routes.cart_url);
      return false;
    }
    this.productId = parsedState.id;
    const sourceDrawer = parsedState.sections?.['cart-drawer']
      ? this.getSectionDOM(parsedState.sections['cart-drawer'], 'cart-drawer')
      : null;
    if (sourceDrawer) this.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      const html = parsedState.sections?.[section.id];
      if (!sectionElement || !html) return;
      const content = this.getSectionDOM(html, section.selector || '.shopify-section');
      if (content) sectionElement.innerHTML = content.innerHTML;
    });

    this.setHeaderCartIconAccessibility();
    this.open();
    return true;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    if (element && !this.contains(element)) this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class IfCartRecommendations extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', (event) => {
      if (event.target.matches('select[name="id"]')) this.updateVariant(event.target);
    });
    this.addEventListener('submit', this.addProduct.bind(this));
  }

  updateVariant(select) {
    const form = select.closest('form');
    const variant = select.selectedOptions[0];
    if (!form || !variant) return;
    form.querySelector('[data-recommendation-price]').textContent = variant.dataset.price;
    const compare = form.querySelector('[data-recommendation-compare]');
    compare.textContent = variant.dataset.compare || '';
    compare.hidden = !variant.dataset.compare;
    form.querySelector('[name="quantity"]').value = variant.dataset.quantity || 1;
    const quantityLabel = form.querySelector('[data-recommendation-quantity]');
    quantityLabel.textContent = ` (${variant.dataset.quantity || 1})`;
    quantityLabel.hidden = Number(variant.dataset.quantity || 1) <= 1;
    const image = form.querySelector('img');
    if (image && variant.dataset.image) {
      image.src = variant.dataset.image;
      image.removeAttribute('srcset');
    }
  }

  async addProduct(event) {
    event.preventDefault();
    const form = event.target;
    if (!form.matches('.if-cart-recommendation') || this.pending) return;
    const drawer = this.closest('cart-drawer');
    if (drawer.getAttribute('aria-busy') === 'true') return;
    const button = form.querySelector('[type="submit"]');
    const error = form.querySelector('[role="alert"]');
    this.pending = true;
    drawer.setBusy(true);
    const buttons = this.querySelectorAll('[type="submit"]');
    buttons.forEach((item) => { item.disabled = true; });
    button.classList.add('loading');
    button.setAttribute('aria-busy', 'true');
    button.querySelector('.loading__spinner')?.classList.remove('hidden');
    error.hidden = true;
    const formData = new FormData(form);
    formData.append('sections', drawer.getSectionsToRender().map((section) => section.id).join(','));
    formData.append('sections_url', window.location.pathname);
    let added = false;
    try {
      const response = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || window.cartStrings.error);
      added = true;
      if (!result.sections?.['cart-drawer']) {
        const sectionResponse = await fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`);
        if (!sectionResponse.ok) throw new Error(window.cartStrings.error);
        result.sections = await sectionResponse.json();
        if (!result.sections['cart-drawer']) throw new Error(window.cartStrings.error);
      }
      if (await drawer.renderContents(result) === false) return;
      const cartData = await CartItems.fetchCartData();
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'if-cart-recommendations', cartData });
    } catch (reason) {
      if (added) {
        window.location.assign(routes.cart_url);
        return;
      }
      error.textContent = reason.message || window.cartStrings.error;
      error.hidden = false;
    } finally {
      this.pending = false;
      drawer.setBusy(false);
      buttons.forEach((item) => { item.disabled = false; });
      button.classList.remove('loading');
      button.removeAttribute('aria-busy');
      button.querySelector('.loading__spinner')?.classList.add('hidden');
    }
  }
}

customElements.define('if-cart-recommendations', IfCartRecommendations);
